import {  Box, Button, Checkbox, FormControl, InputAdornment, Chip, MenuItem, Modal, Select, TextField, Tooltip, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import { handleSave } from "../utils/handleSave";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { createTempId } from "../utils/createTempId";
import { KOT_SELECT_MENU_PROPS, hasInvalidDateValue } from "../utils/formHelpers";
import { KOT_DATE_ERROR_MESSAGE, KOT_DATE_HELPER_TEXT, KOT_DISPLAY_DATE_FORMAT, formatKotDate, parseKotStoredDate, resolveKotDateChange } from "../utils/dateHelpers";
import { COUNTRIES } from "../utils/constants";
import { useKotFormDirty } from "../utils/useKotFormDirty";
import EditIcon from "@mui/icons-material/Edit";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import InventorBulkUploadModal from "../InventorBulkUpload/InventorBulkUploadModal";
import { useDeferredProfileRefresh } from "../utils/useDeferredProfileRefresh";

const PREFIX_OPTIONS = ["", "Mr.", "Mrs.", "Ms.", "Miss", "Dr.", "Prof.", "Hon.", "Mx."];
const SUFFIX_OPTIONS = ["", "Jr.", "Sr.", "II", "III", "IV", "Ph.D.", "M.D.", "Esq."];
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SEARCH_FIELD_OPTIONS = [
  { value: "all", label: "All" },
  { value: "name", label: "Name" },
  { value: "email", label: "Email Address" },
  { value: "nationality", label: "Nationality" },
  { value: "citizenship", label: "Citizenship" },
  { value: "mailingAddress", label: "Mailing Address" },
  { value: "city", label: "Residence City" },
  { value: "state", label: "Residence State" },
  { value: "country", label: "Residence Country" },
  { value: "notes", label: "Notes" },
];

const SectionLabel = ({ children }) => (
  <Typography variant="overline" sx={{ fontWeight: 700, color: "#333", mt: 2, mb: 0.5, display: "block", letterSpacing: "0.05em" }}>
    {children}
  </Typography>
);

const NoInventorsOverlay = () => (
  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
    <Typography>No inventors found</Typography>
  </Box>
);

const emptyFormData = () => ({
  id: "",
  prefix: "",
  firstName: "",
  middleName: "",
  lastName: "",
  suffix: "",
  nationality: "",
  citizenship: "",
  address1: "",
  address2: "",
  address3: "",
  address4: "",
  city: "",
  state: "",
  country: "",
  verifiedDate: "",
  email: "",
  notes: "",
});

const Inventors = ({ mode }) => {
  const isView = mode === "view";
  const buildInventors = (nextInventors = []) => nextInventors.map((inventor) => ({ ...inventor }));
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [dateError, setDateError] = useState(false);
  const savedInventors = useSelector((state) => state.kotClient.inventors);
  const clientId = useSelector((state) => state.kotClient?.clientId);
  const clientNumber = useSelector((state) => state.kotClient?.clientDetails?.clientNumber);
  const [inventors, setInventors] = useState(buildInventors(savedInventors));
  const [selectedRows, setSelectedRows] = useState([]);
  const [formData, setFormData] = useState(emptyFormData());
  const { isDirty, markDirty, markSaved, markReset, syncClean } = useKotFormDirty();

  const [searchField, setSearchField] = useState("all");
  const [searchText, setSearchText] = useState("");

  const countryCodeToLabel = useMemo(() => {
    const map = {};
    Object.entries(COUNTRIES).forEach(([label, code]) => {
      map[code] = label;
    });
    return map;
  }, []);

  const getSearchableValue = (inventor, field) => {
    switch (field) {
      case "name":
        return [inventor.prefix, inventor.firstName, inventor.middleName, inventor.lastName, inventor.suffix].filter(Boolean).join(" ");
      case "email":
        return inventor.email || "";
      case "nationality": {
        const code = inventor.nationality || "";
        const label = countryCodeToLabel[code] || "";
        return [label, code].filter(Boolean).join(" ");
      }
      case "citizenship": {
        const codes = (inventor.citizenship || "").split(", ").filter(Boolean);
        const labels = codes.map((code) => countryCodeToLabel[code] || "");
        return [...labels, ...codes].filter(Boolean).join(" ");
      }
      case "mailingAddress":
        return [inventor.address1, inventor.address2, inventor.address3, inventor.address4].filter(Boolean).join(" ");
      case "city":
        return inventor.city || "";
      case "state":
        return inventor.state || "";
      case "country": {
        const code = inventor.country || "";
        const label = countryCodeToLabel[code] || "";
        return [label, code].filter(Boolean).join(" ");
      }
      case "notes":
        return inventor.notes || "";
      default:
        return "";
    }
  };

  const filteredInventors = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return inventors;

    const ALL_SEARCH_FIELDS = SEARCH_FIELD_OPTIONS.filter((opt) => opt.value !== "all").map((opt) => opt.value);
    const fieldsToCheck = searchField === "all" ? ALL_SEARCH_FIELDS : [searchField];

    return inventors.filter((inventor) => fieldsToCheck.some((field) => getSearchableValue(inventor, field).toLowerCase().includes(query)));
  }, [inventors, searchField, searchText, countryCodeToLabel]);
  const dispatch = useDispatch();

  /**
   * A bulk upload creates inventors server-side, so the grid has to be reloaded from the
   * profile rather than patched locally - re-fetching is also what gets the real
   * inventorIds, which a locally-appended row would not have.
   */
  const { markImported, refreshOnClose } = useDeferredProfileRefresh();

  const handleBulkUploadClose = useCallback(() => {
    setBulkUploadOpen(false);
    refreshOnClose();
  }, [refreshOnClose]);

  useEffect(() => {
    setInventors(buildInventors(savedInventors || []));
    setSelectedRows([]);
    setSearchField("all");
    setSearchText("");
    syncClean();
  }, [clientId, savedInventors]);

  const resetFormData = () => {
    setFormData(emptyFormData());
    setDateError(false);
  };

  const formatVerifiedDate = (value) => formatKotDate(value);

  const baseColumns = [
    { field: "prefix", headerName: "Prefix", width: 80 },
    { field: "firstName", headerName: "First Name", width: 140 },
    { field: "middleName", headerName: "Middle Name", width: 140 },
    { field: "lastName", headerName: "Last Name", width: 140 },
    { field: "suffix", headerName: "Suffix", width: 80 },
    { field: "nationality", headerName: "Nationality", width: 130 },
    { field: "citizenship", headerName: "Citizenship", width: 180 },
    { field: "email", headerName: "Email Address", width: 220 },
    {
      field: "mailingAddress",
      headerName: "Mailing Address",
      width: 280,
      sortable: false,
      renderCell: (params) => {
        const lines = [params.row.address1, params.row.address2, params.row.address3, params.row.address4].filter(Boolean);
        return <span style={{ whiteSpace: "pre-line", lineHeight: 1.4 }}>{lines.join("\n")}</span>;
      },
    },
    { field: "city", headerName: "Residence City", width: 150 },
    { field: "state", headerName: "Residence State", width: 150 },
    { field: "country", headerName: "Residence Country", width: 180 },
    {
      field: "verifiedDate",
      headerName: "Verified On",
      width: 150,
      renderCell: (params) => <span>{formatVerifiedDate(params.row.verifiedDate)}</span>,
    },
    {
      field: "notes",
      headerName: "Notes",
      width: 240,
      sortable: false,
      renderCell: (params) => <span style={{ whiteSpace: "pre-line", lineHeight: 1.4 }}>{params.row.notes}</span>,
    },
  ];

  const actionColumn = {
    field: "actions",
    headerName: "Actions",
    sortable: false,
    width: 70,
    headerClassName: "inventors-actions-header",
    cellClassName: "inventors-actions-cell",
    renderCell: (params) => (
      <IconButton
        size="small"
        onClick={() => {
          setModalMode("edit");
          setFormData(params.row);
          setDateError(false);
          setModalOpen(true);
        }}
      >
        <EditIcon />
      </IconButton>
    ),
  };

  const columns = isView ? baseColumns : [...baseColumns, actionColumn];

  const saveInventor = () => {
    if (formData.email?.trim() && !EMAIL_REGEX.test(formData.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if (dateError || hasInvalidDateValue(formData.verifiedDate)) {
      toast.error(KOT_DATE_ERROR_MESSAGE);
      return;
    }

    if (modalMode === "edit") {
      setInventors((prev) => prev.map((inv) => (inv.id === formData.id ? formData : inv)));
    } else {
      setInventors((prev) => [...prev, { ...formData, id: createTempId() }]);
    }
    markDirty();

    setModalOpen(false);
    resetFormData();
  };

  const saveData = async () => {
    if (inventors.some((inventor) => hasInvalidDateValue(inventor.verifiedDate))) {
      toast.error(KOT_DATE_ERROR_MESSAGE);
      return;
    }

    setIsSaving(true);
    try {
      const result = await handleSave(dispatch, "inventors", inventors, clientId, savedInventors);
      if (result?.status !== "error") {
        markSaved();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    markReset();
    setInventors(buildInventors(savedInventors || []));
    setModalOpen(false);
    resetFormData();
  };

  const deleteSelectedRows = () => {
    markDirty();
    setInventors((prev) => prev.filter((inv) => !selectedRows.includes(inv.id)));
    setSelectedRows([]);
  };

  const countryOptions = useMemo(
    () =>
      Object.entries(COUNTRIES).map(([label, code]) => ({
        label,
        value: code,
      })),
    [],
  );

  const selectedCitizenshipValues = useMemo(
    () =>
      (formData.citizenship || "")
        .split(", ")
        .map((v) => v.trim())
        .filter(Boolean),
    [formData.citizenship],
  );

  return (
    <Box p={4} bgcolor="#fff" borderRadius={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} gap={2}>
        <Typography variant="h3">Inventor Information</Typography>

        <Box display="flex" gap={2} alignItems="center">
          <Box display="flex">
            <FormControl size="small">
              <Select value={searchField} onChange={(e) => setSearchField(e.target.value)} aria-label="Search Field">
                {SEARCH_FIELD_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              inputProps={{
                "aria-label": "Search"
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box display="flex" alignItems="center" gap={1.5}>
          {!isView && selectedRows.length > 0 && (
              <Button color="error" variant="contained" onClick={deleteSelectedRows}>
                Delete ({selectedRows.length})
              </Button>
            )}
        </Box>

          {/* Both sit in the header rather than under the grid: with 100 rows on screen
              the footer is well below the fold, and consulting past uploads shouldn't
              mean starting one. Viewers get the history but not the upload.

              A real link, not an onClick: AddClient's guard intercepts anchor clicks and
              offers to save first, so navigating away can't silently drop grid edits. */}
          {clientId && (
            <Button
              component={RouterLink}
              to={`/kot/client/edit/${clientId}/inventor-validation-history`}
              variant="outlined"
              color="primary"
              startIcon={<HistoryOutlinedIcon />}
            >
              Validation History
            </Button>
          )}

          {!isView && (
            // Reloading the grid from the server after an import would discard unsaved
            // manual edits, so the two cannot be in flight at once.
            <Tooltip title={isDirty ? "Save or cancel your changes before uploading a file." : ""}>
              <span>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<UploadFileOutlinedIcon />}
                  disabled={isDirty}
                  onClick={() => setBulkUploadOpen(true)}
                >
                  Bulk Upload
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box sx={{ width: "100%", background: "#fff", height: 460, maxHeight: 460, overflow: "hidden" }}>
        <DataGrid
          rows={filteredInventors}
          columns={columns}
          columnBuffer={3000}
          checkboxSelection={!isView}
          onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
          rowSelectionModel={selectedRows}
          slots={{ noRowsOverlay: NoInventorsOverlay }}
          getRowHeight={() => "auto"}
          getEstimatedRowHeight={() => 52}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          slotProps={{
            baseCheckbox: {
              inputProps: {
                "data-ignore-dirty": "true",
              },
            },
          }}
          sx={{
            border: 0,
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f7fb",
              fontWeight: "bold",
              borderBottom: "1px solid #ddd",
              position: "relative",
              "&::after": {
                content: '"Actions"',
                position: "absolute",
                top: 0,
                right: 0,
                zIndex: 6,
                display: "flex",
                alignItems: "center",
                boxSizing: "border-box",
                width: "fit-content",
                height: "100%",
                px: 2,
                backgroundColor: "#f5f7fb",
                boxShadow: "-2px 0 4px rgba(0, 0, 0, 0.08)",
              },
            },
            "& .MuiDataGrid-cell": { borderBottom: "1px solid #eee", alignItems: "flex-start", py: 1 },
            "& .MuiDataGrid-columnSeparator": { visibility: "visible" },
            "& .inventors-actions-header": {
              visibility: "hidden",
            },
            "& .inventors-actions-cell": {
              position: "sticky",
              right: 0,
              zIndex: 0,
              backgroundColor: "#fff",
              boxShadow: "-2px 0 4px rgba(0, 0, 0, 0.08)",
            },
            ".MuiTablePagination-displayedRows": { marginTop: "1em", marginBottom: "1em" },
            ".MuiTablePagination-selectLabel": { marginTop: "1em", marginBottom: "1em" },
          }}
        />
      </Box>

      {!isView && (
        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2} mt={2}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              setModalMode("add");
              resetFormData();
              setModalOpen(true);
            }}
          >
            Add More
          </Button>
        </Box>
      )}

      <InventorBulkUploadModal
        open={bulkUploadOpen}
        onClose={handleBulkUploadClose}
        clientProfileId={clientId}
        clientNumber={clientNumber}
        onImported={markImported}
      />


      {!isView && (
        <Box mt={3} display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
          <Button variant="text" onClick={handleCancel} disabled={isSaving || !isDirty}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveData} disabled={isSaving || !isDirty}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </Box>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} sx={{ opacity: 1, mx: "auto", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Box bgcolor="#fff" width={620} borderRadius={2} p={4} sx={{ maxHeight: "90vh", overflowY: "auto" }}>
          <Typography variant="h3" mb={1}>
            {modalMode === "edit" ? "Edit Inventor Information" : "Add Inventor Information"}
          </Typography>

          <SectionLabel>Inventor Legal Name</SectionLabel>
          <Box display="grid" gridTemplateColumns="0.8fr 1.2fr 1.2fr 1.2fr 0.8fr" gap={1} mb={0.5}>
            <TextField disabled={modalMode === "view"} select label="Prefix" name="prefix" value={formData.prefix} onChange={handleChange} fullWidth size="small">
              <MenuItem value="">Select</MenuItem>
              {PREFIX_OPTIONS.map(
                (opt) =>
                  opt && (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ),
              )}
            </TextField>
            <TextField disabled={modalMode === "view"} label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} fullWidth size="small" />
            <TextField disabled={modalMode === "view"} label="Middle Name" name="middleName" value={formData.middleName} onChange={handleChange} fullWidth size="small" />
            <TextField disabled={modalMode === "view"} label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} fullWidth size="small" />
            <TextField disabled={modalMode === "view"} select label="Suffix" name="suffix" value={formData.suffix} onChange={handleChange} fullWidth size="small">
              <MenuItem value="">Select</MenuItem>
              {SUFFIX_OPTIONS.map(
                (opt) =>
                  opt && (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ),
              )}
            </TextField>
          </Box>

          <SectionLabel>Citizenship</SectionLabel>
          <TextField
            select
            label="Select Citizenship"
            fullWidth
            size="small"
            disabled={modalMode === "view"}
            sx={{ mb: 1 }}
            SelectProps={{
              multiple: true,
              value: selectedCitizenshipValues,
              onChange: (event) => {
                const value = event.target.value;
                const selectedValues = typeof value === "string" ? value.split(",") : value;

                setFormData((prev) => ({
                  ...prev,
                  citizenship: selectedValues.join(", "),
                }));
              },
              renderValue: (selected) => (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                  }}
                >
                  {selected.map((value) => {
                    const option = countryOptions.find((o) => o.value === value);
                    return (
                      <Chip
                        key={value}
                        label={option?.label || value}
                        size="small"
                        onMouseDown={(e) => e.stopPropagation()} // Prevent menu from opening
                        onDelete={() => {
                          const updatedValues = selected.filter((v) => v !== value);

                          setFormData((prev) => ({
                            ...prev,
                            citizenship: updatedValues.join(", "),
                          }));
                        }}
                      />
                    );
                  })}
                </Box>
              ),
              MenuProps: {
                PaperProps: {
                  style: { maxHeight: 300 },
                },
                disableScrollLock: true,
              },
            }}
          >
            {countryOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox checked={selectedCitizenshipValues.includes(option.value)} />
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <SectionLabel>Nationality</SectionLabel>
          <TextField disabled={modalMode === "view"} select label="Select Nationality" name="nationality" value={formData.nationality} onChange={handleChange} fullWidth size="small" sx={{ mb: 1 }} SelectProps={KOT_SELECT_MENU_PROPS}>
            <MenuItem value="">Select</MenuItem>
            {Object.entries(COUNTRIES).map(([key, code]) => (
              <MenuItem key={code} value={code}>
                {key}
              </MenuItem>
            ))}
          </TextField>

          <SectionLabel>Residence Information</SectionLabel>
          <Box display="flex" flexDirection="column" gap={1} mb={1}>
            <TextField disabled={modalMode === "view"} label="City" name="city" value={formData.city} onChange={handleChange} fullWidth size="small" />
            <TextField disabled={modalMode === "view"} label="State" name="state" value={formData.state} onChange={handleChange} fullWidth size="small" />
            <TextField disabled={modalMode === "view"} select label="Country" name="country" value={formData.country} onChange={handleChange} fullWidth size="small" SelectProps={KOT_SELECT_MENU_PROPS}>
              <MenuItem value="">Select</MenuItem>
              {Object.entries(COUNTRIES).map(([key, code]) => (
                <MenuItem key={code} value={code}>
                  {key}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <SectionLabel>Mailing Address</SectionLabel>
          <Box display="flex" flexDirection="column" gap={1} mb={1}>
            <TextField disabled={modalMode === "view"} label="Mailing Address Line 1" name="address1" value={formData.address1} onChange={handleChange} fullWidth size="small" />
            <TextField disabled={modalMode === "view"} label="Mailing Address Line 2" name="address2" value={formData.address2} onChange={handleChange} fullWidth size="small" />
            <TextField disabled={modalMode === "view"} label="Mailing Address Line 3" name="address3" value={formData.address3} onChange={handleChange} fullWidth size="small" />
            <TextField disabled={modalMode === "view"} label="Mailing Address Line 4" name="address4" value={formData.address4} onChange={handleChange} fullWidth size="small" />
          </Box>

          <SectionLabel>Contact Information</SectionLabel>
          <TextField disabled={modalMode === "view"} label="Email Address" name="email" value={formData.email} onChange={handleChange} fullWidth size="small" sx={{ mb: 1 }} error={Boolean(formData.email?.trim()) && !EMAIL_REGEX.test(formData.email.trim())} helperText={Boolean(formData.email?.trim()) && !EMAIL_REGEX.test(formData.email.trim()) ? "Enter a valid email address" : ""} />

          <SectionLabel>KOT Internal Field</SectionLabel>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label="Select Verified Date"
              value={parseKotStoredDate(formData.verifiedDate)}
              onChange={(newValue) => {
                // The picker only validates the controlled `value` prop, so a date it cannot
                // parse never reaches `onError`. Flag it from here instead, otherwise the
                // field shows the bad text while the old value saves silently. Typing is
                // blocked below, but Backspace still leaves partial text, and a paste from
                // the context menu never fires a keydown at all.
                const { isInvalid, shouldStore, storedDate } = resolveKotDateChange(newValue);
                setDateError(isInvalid);
                if (shouldStore) {
                  setFormData((prev) => ({ ...prev, verifiedDate: storedDate }));
                }
              }}
              onError={(reason) => setDateError(Boolean(reason))}
              inputFormat={KOT_DISPLAY_DATE_FORMAT}
              renderInput={(pickerParams) => (
                <TextField
                  {...pickerParams}
                  disabled={modalMode === "view"}
                  fullWidth
                  size="small"
                  placeholder="Select Verified Date"
                  error={dateError || hasInvalidDateValue(formData.verifiedDate) || Boolean(pickerParams.error)}
                  helperText={dateError || hasInvalidDateValue(formData.verifiedDate) ? KOT_DATE_HELPER_TEXT : pickerParams.helperText}
                  // Calendar-only entry, matching every other KOT date field. Backspace and
                  // Delete stay open so the field can still be cleared; Tab and Enter so
                  // keyboard navigation and the picker's own shortcuts keep working.
                  onKeyDown={(e) => {
                    if (e.key !== "Backspace" && e.key !== "Delete" && e.key !== "Tab" && e.key !== "Enter") {
                      e.preventDefault();
                    }
                  }}
                />
              )}
            />
          </LocalizationProvider>

          <SectionLabel>Notes</SectionLabel>
          <TextField disabled={modalMode === "view"} label="Notes" name="notes" value={formData.notes} onChange={handleChange} fullWidth size="small" multiline minRows={2} />

          <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="text" onClick={() => setModalOpen(false)}>
              {modalMode === "view" ? "Close" : "Cancel"}
            </Button>
            {modalMode !== "view" && (
              <Button variant="contained" onClick={saveInventor}>
                {modalMode === "edit" ? "Update" : "Add"}
              </Button>
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default Inventors;
